import type { LayerState } from '@takeshape/schema';
import { createRoutine } from 'app/common/utils/new-routines';
import { showMessage } from 'app/messages/actions';
import { getCurrentProjectId } from 'app/project/selectors';
import hash from 'object-hash';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { getRoutineSaga } from '../../common/utils/new-sagas';
import { getAllServiceLayersQuery } from '../api';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from '../constants';
import { getBranchByName, getBranchQueryArgs } from '../selectors';
import type { Branch } from '../types';

export type FetchServiceLayersMeta = {
  branch?: string;
};

export type FetchServiceLayersPayload = {
  getAllServiceLayers: {
    servicesHash: string;
    layers: LayerState[];
  };
};

export const fetchServiceLayersRoutine = createRoutine('branches/FETCH_SERVICE_LAYERS')<
  FetchServiceLayersMeta,
  undefined,
  undefined,
  FetchServiceLayersPayload,
  string,
  undefined
>();

export function* fetchServiceLayersSaga(_: unknown, meta: FetchServiceLayersMeta): SagaIterator {
  const projectId = yield select(getCurrentProjectId);

  if (!projectId) return null;

  const branchArgs = yield select(getBranchQueryArgs, {
    branchName: meta.branch
  });
  return yield call(getAllServiceLayersQuery, branchArgs);
}

export function* fetchServiceLayersRejectedSaga({
  meta: { branch },
  payload
}: ActionType<typeof fetchServiceLayersRoutine.rejected>): SagaIterator {
  if (branch !== SCRATCH_BRANCH) {
    console.error(`Could not fetch ${branch} service schema:\n\n`, payload);
    yield put(
      showMessage('error', `Could not fetch ${branch} service schema. Please see dev console for more details.`)
    );
  }
}

export const fetchServiceLayersRoutineSaga = getRoutineSaga(fetchServiceLayersRoutine, fetchServiceLayersSaga);

export function servicesHaventChanged(branchState: Branch | undefined) {
  const services = branchState?.layers.user.data?.services;
  const servicesHash = services && hash(services, { algorithm: 'md5' });
  return servicesHash ? branchState?.serviceLayers.hash === servicesHash : false;
}

export function* fetchServiceLayersIfNotLoading(
  action: ActionType<typeof fetchServiceLayersRoutine.requested>
): SagaIterator {
  const { branch = DEFAULT_BRANCH } = action.meta;
  const projectId = yield select(getCurrentProjectId);
  if (!projectId) {
    return null;
  }

  const branchState: Branch | undefined = yield select(getBranchByName, branch);

  if (branchState?.serviceLayers.loading || servicesHaventChanged(branchState)) {
    return;
  }

  return yield call(fetchServiceLayersRoutineSaga, action);
}

export default function* root(): SagaIterator {
  yield all([
    takeEvery(fetchServiceLayersRoutine.requested, fetchServiceLayersIfNotLoading),
    takeEvery(fetchServiceLayersRoutine.rejected, fetchServiceLayersRejectedSaga)
  ]);
}
